/**
 * Endpoints the Focus bar reads: Focus (apps/api/api/services/FOCUS.md),
 * account availability (also the source of the account's outcome windows),
 * filter option lists and ad-network accounts. Types keep only what the UI
 * reads and match the Pydantic contracts. Every screen shares these queries,
 * so switching screens never refetches the Focus.
 */
import { queryOptions } from '@tanstack/react-query';
import type { WindowDays } from './dates';
import { paramsKey, type FocusApiParams } from './search';
import { api } from '@/api/client';

// ------------------------------------------------------------------ focus --

export type FocusEvidence = 'enough' | 'enough_at_28' | 'not_enough';

export interface FocusWindow {
  window_days: number;
  date_from: string | null;
  date_to: string | null;
  spend: number;
  /** Creatives with any delivery in the window. */
  delivered_creatives?: number;
  /** Creatives that clear the per-creative floors (`minimums`), and their spend. */
  qualified_creatives?: number;
  qualified_spend?: number;
  sufficient: boolean;
}

/** The evidence floors behind "Enough data" (FOCUS.md "Evidence"). */
export interface FocusMinimums {
  creative_impressions: number;
  creative_spend: number;
  creatives: number;
  spend: number;
}

export interface FocusApp {
  /** `appId::OS`, the value every reporting route accepts as `app_id`. */
  id: string;
  app_id: string;
  os: string;
  name: string;
  icon_url: string | null;
  spend: number;
  latest_data_day: string | null;
  fresh: boolean;
  evidence: FocusEvidence;
  windows: FocusWindow[];
}

export interface FocusAppsResponse {
  ranking_window_days: number;
  minimums?: FocusMinimums;
  apps: FocusApp[];
}

export interface FocusReplacement {
  reason: string;
  message: string;
}

export interface FocusResponse {
  source: 'stored' | 'recommended' | 'none';
  app_ids: string[];
  window_days: WindowDays | null;
  date_from: string | null;
  date_to: string | null;
  evidence: FocusEvidence | null;
  replaced: FocusReplacement | null;
}

// ------------------------------------------------------------ availability --

export type AvailabilityState = 'no_connections' | 'no_enabled_accounts' | 'no_entitled_apps' | 'no_reportable_data' | 'available';

/**
 * States where the account has no data of its own yet, so it gets the whole
 * product on sample data instead of a blank screen. (No entitled apps is a
 * choice the account made in Billing, not a missing setup step.)
 */
const NO_OWN_DATA: ReadonlySet<AvailabilityState> = new Set(['no_connections', 'no_enabled_accounts', 'no_reportable_data']);

export const hasNoDataOfItsOwn = (state: AvailabilityState | undefined): boolean => state !== undefined && NO_OWN_DATA.has(state);

export interface OutcomeWindow {
  window_id: string;
  metric_suffix: string;
  label: string;
  basis: string;
  maturity_days: number;
  conversions_available: boolean;
  conversions_value_available: boolean;
}

export interface PerformanceAvailability {
  state: AvailabilityState;
  outcome_windows: OutcomeWindow[];
}

// ----------------------------------------------------------- filter values --

export type FilterType = 'date_range' | 'campaign_name' | 'ad_group_name' | 'ad_group_ad_name' | 'platform' | 'country' | 'os';

export interface DateRangeValues {
  date_from: string | null;
  date_to: string | null;
}
export interface StringValues {
  values: string[];
}
export interface CountryValues {
  country_code: string[];
  country_name: string[];
}

/** One option list, normalised: value + optional API-supplied label. */
export interface OptionList {
  values: { value: string; label?: string }[];
  /** The API capped the list at `max_array_size`. */
  truncated: boolean;
}

/** `PerformanceFilterValues.max_array_size` default. */
export const FILTER_VALUES_CAP = 3000;

// ------------------------------------------------------------- ad accounts --

export interface AdNetworkAccount {
  id: string;
  type: string;
  name: string | null;
  enabled: boolean;
  deleted: boolean;
}

// -------------------------------------------------------------------- api --

export const focusApi = {
  focusApps: (signal?: AbortSignal) => api.get<FocusAppsResponse>('/api/admaster/focus/apps', { signal }),
  focus: (signal?: AbortSignal) => api.get<FocusResponse>('/api/admaster/focus', { signal }),
  saveFocus: (body: { app_ids: string[]; window_days: WindowDays }) => api.put<FocusResponse>('/api/admaster/focus', body),
  availability: (signal?: AbortSignal) => api.get<PerformanceAvailability>('/api/admaster/asset/performance/availability', { signal }),
  accounts: (signal?: AbortSignal) => api.get<AdNetworkAccount[]>('/api/admaster/account', { signal }),
  filterValues: <T>(filterType: FilterType, params: FocusApiParams, signal?: AbortSignal) =>
    api.get<T>('/api/admaster/asset/filter-values', { query: { ...params, filter_type: filterType }, signal }),
};

// ---------------------------------------------------------------- queries --

export const focusKeys = {
  all: ['focus'] as const,
  focusApps: () => [...focusKeys.all, 'focus-apps'] as const,
  focus: () => [...focusKeys.all, 'focus'] as const,
  availability: () => [...focusKeys.all, 'availability'] as const,
  accounts: () => [...focusKeys.all, 'accounts'] as const,
  filterValues: (filterType: FilterType, params: FocusApiParams) =>
    [...focusKeys.all, 'filter-values', filterType, paramsKey(params)] as const,
};

export const focusAppsQuery = () =>
  queryOptions({ queryKey: focusKeys.focusApps(), queryFn: ({ signal }) => focusApi.focusApps(signal), staleTime: 5 * 60_000 });

/** Read once per visit: a later GET would not reflect a PUT made meanwhile anyway. */
export const focusQuery = () =>
  queryOptions({ queryKey: focusKeys.focus(), queryFn: ({ signal }) => focusApi.focus(signal), staleTime: Infinity, placeholderData: undefined });

export const availabilityQuery = () =>
  queryOptions({ queryKey: focusKeys.availability(), queryFn: ({ signal }) => focusApi.availability(signal), staleTime: 5 * 60_000 });

export const accountsQuery = () =>
  queryOptions({
    queryKey: focusKeys.accounts(),
    queryFn: ({ signal }) => focusApi.accounts(signal),
    staleTime: 5 * 60_000,
    // Enabled, undeleted accounts only — the same population the reports use.
    select: (accounts: AdNetworkAccount[]) => accounts.filter((a) => a.enabled && !a.deleted),
  });

/** First and last data day for the scope (dates are ignored by the API for this type). */
export const dateBoundsQuery = (params: FocusApiParams) =>
  queryOptions({
    queryKey: focusKeys.filterValues('date_range', params),
    queryFn: ({ signal }) => focusApi.filterValues<DateRangeValues>('date_range', params, signal),
    // A new scope's bounds are a different fact: never show the previous scope's.
    placeholderData: undefined,
  });

const toOptionList = (filterType: FilterType, data: StringValues | CountryValues): OptionList => {
  if (filterType === 'country' && 'country_code' in data) {
    return {
      values: data.country_code.map((code, i) => ({ value: code, label: data.country_name[i] ?? code })),
      truncated: data.country_code.length >= FILTER_VALUES_CAP,
    };
  }
  const values = 'values' in data ? data.values : [];
  return { values: values.map((value) => ({ value })), truncated: values.length >= FILTER_VALUES_CAP };
};

export const optionListQuery = (filterType: Exclude<FilterType, 'date_range'>, params: FocusApiParams, enabled: boolean) =>
  queryOptions({
    queryKey: focusKeys.filterValues(filterType, params),
    queryFn: async ({ signal }) => toOptionList(filterType, await focusApi.filterValues<StringValues | CountryValues>(filterType, params, signal)),
    enabled,
    placeholderData: undefined,
  });
